<template>
  <div>
    <b-modal body-class="p-0" size="md" title="Payment Details" v-model="showPaymentModal" hide-footer hide-header>

      <vue-plyr v-if="isVideo">
        <video controls
               class="w-100"
               :data-poster="assetThumbUrl">
          <source :src="assetMediaUrl" type="video/mp4"/>
        </video>
      </vue-plyr>
      <div v-else class="img-bg w-100 rounded-top" style="height: 240px"
           :style="`background-image: url('${assetThumbUrl}')`"></div>

      <div class="text-center">
        <div class="d-flex p-2 align-items-center justify-content-center" v-if="!publicPaymentCompleted">
          <b-form-select
            class="mr-2 w-auto"
            v-model="selectedVersion"
            :options="publicVersionOptions"/>

          <h6 class="m-0 font-weight-semi-bold">{{ versionSizeName(selectedVersion, true) }} - {{ assetType }}</h6>
        </div>
        <div class="pt-3" v-else>
          <h6 class="m-0 font-weight-semi-bold">{{ versionSizeName(selectedVersion) }} - {{ assetType }}</h6>
        </div>

        <p class="m-0 font-weight-medium text-gray-500">Royalty-Free Unlimited Worldwide license</p>
        <h2 class="font-weight-semi-bold" v-if="!publicPaymentCompleted">
          ${{ currentProduct.cost / 100 }}
        </h2>
      </div>

      <hr class="w-100">

      <!-- v-show because it has a setTimeout for downloadLink -->
      <div class="p-3" v-show="!publicPaymentCompleted">
        <public-purchase-form
          :current-product-id="currentProductId"
          :asset-id="assetId"
          product-type="asset"
          button-title="Complete Purchase"
          @downloadLink="handleDownloadLink"
          @paymentCompleted="handlePaymentCompleted"/>

        <div class="text-center">
          <h6 class="font-weight-semi-bold">
            By purchasing a license of this asset, you agree to POCSTOCK's
            <a href="/terms" target="_blank">Terms of Service</a>
            and
            <a href="/legal/privacy" target="_blank">Privacy Policy</a>
          </h6>
          <img :src="secureImageUrl">
        </div>

      </div>

      <div class="px-3 pb-3 d-flex flex-column align-items-center" v-if="publicPaymentCompleted">
        <i class="far fa-3x fa-check-circle text-success m-2"></i>
        <h4 class="text-center font-weight-bold">Thank you for <br> your order</h4>
        <p class="text-gray-500 text-center">Check your email. We've sent instructions to setup your account</p>

        <form ref="downloadForm" v-if="downloadLink" :action="downloadLink" method="post">
          <input type="hidden" :value="csrfToken" name="authenticity_token"/>
          <button @click="handleDownloadForm" :disabled="downloaded"
                  class="btn btn-primary px-5 py-2 mb-4 d-flex align-items-center">
            <span class="mr-2">Download Asset</span>
            <i class="fa fa-download fa-lg"></i>
          </button>
        </form>

        <pulse-loader :loading="!downloadLink"
                      v-else
                      size="10px"
                      color="#00C0F4"
                      class="mx-2"/>

        <div class="text-center">
          <h6 class="font-weight-semi-bold">
            Before using, please review our
            <a href="/license-agreement-standard" target="_blank">License Agreement</a>
          </h6>
        </div>
      </div>

    </b-modal>

    <!--  Version size variants - Radios -->
    <h6>{{ chooseSizeLabel }}</h6>
    <ul class="list-group poc-list-group">
      <li v-for="versionSize in creditVersionSizeProducts"
          class="list-group-item d-flex justify-content-between"
          :class="{'actionable' : !isVersionOwned(versionSize), 'active': selectedVersion === versionSize}">
        <b-form-radio class="w-100" v-model="selectedVersion" :value="versionSize"
                      :disabled="isVersionOwned(versionSize)">
          <div class="d-flex justify-content-between">
            <span>{{ versionSizeName(versionSize) }}</span>
            <span v-if="showCurrencyCost(versionSize)">
              ${{ productByVersionAndPaymentMethod(versionSize, 'currency').cost / 100 }}
            </span>
          </div>
        </b-form-radio>
        <a :href="versionSizedAssetDownloadUrl(versionSize)"
           v-b-tooltip
           target="_blank"
           v-if="isVersionOwned(versionSize)"
           :title="`Download ${versionSizeName(versionSize)}`">
          <i class="fas fa-download"></i>
        </a>
      </li>
    </ul>

    <!--  Request to purchase form - for browser users -->
    <div v-if="isBrowser && selectedVersion">
      <div v-if="requestPurchaseCreated">
        <div class="mt-3 text-center font-italic">
          <span>Purchase Request Sent on {{ requestPurchaseCreated.created_at_formatted }}</span>
          <span v-if="requestPurchaseCreated.assigned_to_name"> to {{ requestPurchaseCreated.assigned_to_name }}</span>
          <span v-if="requestPurchaseCreatedStatus"> - {{ requestPurchaseCreatedStatus }}</span>
        </div>
      </div>
      <div v-else>
        <h6 class="mt-3">Request to Purchase</h6>
        <b-form v-if="userId" class="mt-2" method="post" action="/customers/purchase_requests">
          <input type="hidden" :value="csrfToken" name="authenticity_token"/>
          <input type="hidden" :value="selectedVersion" name="purchase_request[version]"/>
          <input type="hidden" :value="assetId" name="purchase_request[asset_id]"/>

          <div v-if="agencyFormRequired">
            <b-form-group label="JOB ID">
              <b-input name="purchase_request[job_id]" v-model="jobId"></b-input>
            </b-form-group>

            <b-form-group label="PURCHASE ORDER NUMBER">
              <b-input name="purchase_request[po_number]" v-model="poNumber"></b-input>
            </b-form-group>

            <template v-if="isAgency">
              <input type="hidden" :value="licensee" name="purchase_request[licensee]"/>
              <b-form-group label="CLIENT / LICENSEE">
                <v-select v-model="licensee"
                          @search="fetchLicensees"
                          :options="licenseesOptions"
                          push-tags
                          taggable
                          :filterable="false">
                  <template #no-options>
                    type to search licensee...
                  </template>
                  <template #option="lic">
                    {{ lic.label }}
                  </template>
                  <template #selected-option="lic">
                    {{ lic.label }}
                  </template>
                </v-select>
              </b-form-group>
            </template>
            <template v-else-if="isEnterprise">
              <b-form-group label="LICENSEE">
                <b-input readonly name="purchase_request[licensee]" v-model="licensee"></b-input>
              </b-form-group>
            </template>


            <b-form-group label="BUYER">
              <b-form-select name="purchase_request[assigned_to_id]" v-model="selectedBuyer"
                             :options="buyersSelectOptions"></b-form-select>
            </b-form-group>
          </div>

          <div class="form-group" v-if="selectedVersion === 'comp'">
            <div class="alert alert-danger">
              <b>Limitations on Use</b> Unwatermarked Comps can only be used under composite license for limited
              internal
              non-commercial use.
            </div>
          </div>

          <b-button type="submit"
                    variant="primary"
                    :disabled="!isBrowserFormValid"
                    block
                    size="lg">
            {{ requestToPurchaseTitle }}
          </b-button>
        </b-form>
      </div>

    </div>

    <!--  Logged in / pay with CC/Sub - payment method radios  -->
    <div v-if="availablePaymentMethods.length > 0 && userId">
      <h6 class="mt-4">Choose Payment Method</h6>
      <ul class="list-group poc-list-group">

        <li v-for="option in availablePaymentMethods"
            @click="selectedPaymentMethod = option"
            :class="{'active': selectedPaymentMethod === option}"
            class="list-group-item actionable">

          <b-form-radio v-model="selectedPaymentMethod" :value="option" v-if="option === 'currency'">
            <div class="d-flex justify-content-between">
              <div>Pay by Credit Card</div>
              <div v-if="selectedVersion">
                ${{ productByVersionAndPaymentMethod(selectedVersion, option).cost / 100 }}
              </div>
            </div>
          </b-form-radio>

          <b-form-radio v-model="selectedPaymentMethod" :value="option"
                        v-else-if="option === 'subscription_token'">
            <div class="d-flex justify-content-between">
              <div>Pay using Subscription</div>
            </div>
          </b-form-radio>
        </li>

        <li class="list-group-item actionable" v-if="newSubscriptionOptionVisible">
          <a href="/pricing">
            {{ newSubscriptionOptionText }}
          </a>
        </li>
      </ul>
    </div>

    <!--  Actual form to submit -->
    <div v-if="availablePaymentMethods.length > 0">
      <!-- logged in -->
      <b-form v-if="userId" class="mt-4" method="post" :action="currentParamsOptions.formUrl">
        <input type="hidden" :value="csrfToken" name="authenticity_token"/>
        <input type="hidden" :value="currentProductId" :name="currentParamsOptions.paramName"/>
        <input type="hidden" :value="assetId" :name="currentParamsOptions.assetIdParamName"/>

        <div class="form-group" v-if="canChooseCustomLicenseAgreement">
          <input type="hidden" v-if="customAgreementSelected"
                 :value="licenseAgreement"
                 :name="currentParamsOptions.licenseAgreementParamName">
          <h6>Choose License Agreement</h6>
          <b-form-select :options="availableLicenseAgreements" v-model="licenseAgreement"/>
        </div>

        <div class="form-group" v-if="selectedVersion === 'comp'">
          <div class="alert alert-danger">
            <b>Limitations on Use</b> Unwatermarked Comps can only be used under composite license for limited internal
            non-commercial use.
          </div>
        </div>


        <div v-if="agencyFormRequired">
          <b-form-group label="JOB ID">
            <b-input name="purchase_request[job_id]" v-model="jobId"></b-input>
          </b-form-group>

          <b-form-group label="PURCHASE ORDER NUMBER">
            <b-input name="purchase_request[po_number]" v-model="poNumber"></b-input>
          </b-form-group>

          <input type="hidden" :value="licensee" name="purchase_request[licensee]"/>
          <input type="hidden" :value="licensee" :name="currentParamsOptions.licenseeParamName"
                 v-if="currentParamsOptions.licenseeParamName"/>
          <input type="hidden" :value="jobId" :name="currentParamsOptions.jobIdParamName"
                 v-if="currentParamsOptions.jobIdParamName"/>
          <input type="hidden" :value="poNumber" :name="currentParamsOptions.poNumberParamName"
                 v-if="currentParamsOptions.poNumberParamName"/>

          <template v-if="isAgency">
            <b-form-group label="CLIENT / LICENSEE">
              <v-select v-model="licensee"
                        @search="fetchLicensees"
                        :options="licenseesOptions"
                        push-tags
                        taggable
                        :filterable="false">
                <template #no-options>
                  type to search licensee...
                </template>
                <template #option="lic">
                  {{ lic.label }}
                </template>
                <template #selected-option="lic">
                  {{ lic.label }}
                </template>
              </v-select>
            </b-form-group>
          </template>
          <template v-else-if="isEnterprise">
            <b-form-group label="LICENSEE">
              <b-input readonly name="purchase_request[licensee]" v-model="licensee"></b-input>
            </b-form-group>
          </template>

        </div>

        <b-button type="submit"
                  variant="primary"
                  :disabled="!currentProduct.id || !isBuyerFormValid"
                  block
                  size="lg">
          {{ loggedInFormButtonTitle }}
        </b-button>

      </b-form>

      <!-- logged out -->
      <b-button v-else
                variant="primary"
                class="mt-4"
                block
                @click="showPaymentModal = true"
                size="lg"> PURCHASE A LICENSE
      </b-button>
    </div>

    <div v-if="license" class="mt-3 text-center font-italic">
      This asset was purchased on {{ license.created_at }} under
      <a target="_blank" :href="`/customers/licenses/${license.id}`">{{ license.agreement_name }}</a>
    </div>

    <div v-if="compLicense" class="mt-3 text-center font-italic">
      This asset was purchased on {{ compLicense.created_at }} under
      <a target="_blank" :href="`/customers/licenses/${compLicense.id}`">Unwatermarked Comp License Agreement</a>
    </div>

  </div>
</template>

<script>
import uniq from 'lodash/uniq';
import startCase from 'lodash/startCase';
import GeneralMixins from "../../../mixins/GeneralMixins";
import PublicPurchaseForm from "./PublicPurchaseForm";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import AssetLoader from "../../../../AssetLoader";
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes";
import vSelect from "vue-select";
import {OrganizationsApi} from "../../Api/OrganizationsApi.js";

export default {
  components: {PublicPurchaseForm, PulseLoader, vSelect},
  props: [
    'products',
    'license',
    'compLicense',
    'licenseAgreements',
    'paymentsUrl',
    'creditExchangesUrl',
    'subscriptionExchangesUrl',
    'assetDownloadUrl',
    'assetId',
    'assetThumbUrl',
    'assetMediaUrl',
    'availableCredits',
    'availableAssetSizes',
    'defaultProductVersion',
    'activeSubscription', // true if there's an active subscription, regardless of available token count,
    'activeSubscriptionSigned',
    'permissions',
    'imageSizes',
    'imageSizeHierarchy',
    'assetUsage',
    'assetType',
    'sizeNames',
    'subscriptionProducts',
    'userId' // present if user is logged in
  ],

  data() {
    return {
      selectedVersion: null,
      selectedPaymentMethod: null,
      licenseAgreement: null,
      // Specific agency buyer fields
      jobId: null,
      poNumber: null,
      licensee: null,
      selectedBuyer: null,
      showPaymentModal: false,
      publicPaymentCompleted: false,
      email: null,
      downloadLink: null,
      downloaded: false,
      licenseesOptions: []
    }
  },

  async mounted() {
    if (this.userId) {
      const organization = await OrganizationsApi.getOrganization()
      if (this.isEnterprise) {
        this.licensee = organization.name
      }
    }
  },

  methods: {

    fetchLicensees(search, loading) {
      loading(true)
      this.searchLicensees(loading, search, this)
    },

    searchLicensees: _.debounce((loading, search, vm) => {
      const url = `/customers/licensees?query=${encodeURIComponent(search)}`
      vm.$http.get(url).then(fulfilled => {
        vm.licenseesOptions = fulfilled.data
        loading(false)
      })
    }, 150),

    handleDownloadForm() {
      this.$refs.downloadForm.submit()
      this.downloaded = true
    },

    handlePaymentCompleted(data) {
      // this.showPaymentModal = false
      this.email = data.email
      // The following hides the public-purchase form which has a setTimeout
      this.publicPaymentCompleted = true
    },

    handleDownloadLink(data) {
      if (data.url) {
        this.downloadLink = data.url
      }
    },

    sortArray(array, sortingArray) {
      return array.sort((t1, t2) => {
        const index1 = sortingArray.indexOf(t1);
        const index2 = sortingArray.indexOf(t2);
        return ((index1 > -1 ? index1 : Infinity) - (index2 > -1 ? index2 : Infinity));
      })
    },

    isVersionOwned(version) {
      if (version === 'comp' && this.compLicense) {
        return true
      }

      if (!this.license || !version) return false;

      const size = this.license.valid_asset_version_sizes.find(v => v === version);
      return size !== null && size !== undefined;
    },

    versionSizedAssetDownloadUrl(version) {
      return `${this.assetDownloadUrl}?version_size=${version}`
    },

    versionSizeName(version, suffixOnly = false) {
      if (version === 'comp') {
        return "Unwatermarked Comp (Internal Use Only)"
      }
      const versionName = this.sizeNames[version] || ""
      let suffix = null

      if (this.isVideo) {
        if (this.imageSizes && this.imageSizes[this.assetUsage] && this.imageSizes[this.assetUsage][version]) {
          const versionSize = this.imageSizes[this.assetUsage][version];
          if (versionSize.width && versionSize.height) {
            suffix = `${versionSize.width} x ${versionSize.height}`
          }
        }
      } else if (this.isIllustration) {
        suffix = null
      } else {
        if (this.assetUsage === 'editorial') {
          suffix = "Original size (over 3000 pixels)"
        } else if (this.imageSizes && this.imageSizes[this.assetUsage] && this.imageSizes[this.assetUsage][version]) {
          const versionSize = this.imageSizes[this.assetUsage][version];

          if (versionSize.size) {
            const description = this.products.find(p => p.asset_version_size === version).description;
            if (description) {
              suffix = `${description} - Up to ${versionSize.size} pixels - ${versionSize.dpi} dpi`;
            } else {
              suffix = `Up to ${versionSize.size} pixels - ${versionSize.dpi} dpi`
            }
          } else if (this.largeSizeMetadata && this.largeSizeMetadata.size) {
            suffix = `Original size (over ${this.largeSizeMetadata.size} pixels)`;
          } else {
            suffix = `Original size`;
          }
        }
      }


      if (suffix) {
        if (suffixOnly) {
          return suffix
        } else {
          return `${versionName} - ${suffix}`
        }
      } else {
        return versionName
      }
    },

    productByVersionAndPaymentMethod(version, paymentMethod) {
      return this.products.find(p =>
        p.cost_type === paymentMethod &&
        p.product_asset_types
          .filter(t => t.asset_type === this.assetType.toLowerCase())
          .map(t => t.version_size)
          .includes(version)
      )
    },

    defaultSelectedVersion() {
      if (!this.isVersionOwned(this.defaultProductVersion)) {
        return this.defaultProductVersion;
      }
      return null;
    },

    defaultSelectedPaymentMethod() {
      if (!this.selectedVersion) return null;
      return this.payableWithSubscription(this.selectedVersion) ? 'subscription_token' : 'currency'
    },

    payableWithSubscription(version) {
      if (!version) return false
      const versions = this.availableSubscriptionProductVersions
      if (versions.length === 0) return false
      const hierarchyVersionIndex = this.imageSizeHierarchy.indexOf(version)
      const indexes = versions.map(v => this.imageSizeHierarchy.indexOf(v))
      const index = indexes.find(idx => idx >= hierarchyVersionIndex)
      return index !== null && index !== undefined
    },

    showCurrencyCost(version) {
      if (version === 'comp') return false; // comps are not in the hierarchy and are not payable with currency - buyers will get a NaN if allowed to pass
      if (!this.userId) return true; //logged out users always see currency
      if (this.isBrowser) return false; // logged in browsers never see currency
      if (this.isVersionOwned(version)) return false;
      return !this.payableWithSubscription(version)
    },

    selectDefaultLicenseAgreement() {
      const agreement = this.availableLicenseAgreements[this.availableLicenseAgreements.length - 1]
      if (agreement) {
        this.licenseAgreement = agreement.value
      }
    }
  },

  created() {
    this.selectedVersion = this.defaultSelectedVersion();
    this.selectedPaymentMethod = this.defaultSelectedPaymentMethod();
    this.selectDefaultLicenseAgreement()
  },

  computed: {

    requestToPurchaseTitle() {
      if (this.selectedVersion === 'comp') {
        return "DOWNLOAD COMP"
      } else {
        return "REQUEST TO PURCHASE"
      }
    },

    loggedInFormButtonTitle() {
      if (this.selectedVersion === 'comp') {
        return "DOWNLOAD COMP"
      } else {
        return "PURCHASE A LICENSE"
      }
    },

    isAgency() {
      return this.permissions.organization_type === 'agency'
    },

    isEnterprise() {
      return this.permissions.organization_type === 'enterprise'
    },

    isBrowser() {
      return this.permissions.customer_browser
    },

    agencyFormRequired() {
      return (this.isAgency || this.isEnterprise) && (this.selectedVersion !== 'comp' || (this.selectedVersion === 'comp' && !this.permissions.auto_approve_comp))
    },

    chooseSizeLabel() {
      if (this.isVideo) {
        return "Choose Resolution"
      } else {
        return `Choose ${this.assetType} Size`
      }
    },

    isVideo() {
      return this.assetType === "Video"
    },

    isIllustration() {
      return this.assetType === "Illustration"
    },

    secureImageUrl: function () {
      return AssetLoader.imagePath(`secure.png`);
    },

    csrfToken: function () {
      return window.POC.csrfToken()
    },

    publicVersionOptions() {
      return this.versionSizeProducts.map(size => {
        return {
          value: size,
          text: this.sizeNames[size]
        }
      })
    },

    isBrowserFormValid() {
      if (this.agencyFormRequired) {
        return this.selectedBuyer && this.jobId && this.licensee
      }

      return true
    },

    isBuyerFormValid() {
      if (this.agencyFormRequired) {
        if (this.isEnterprise) {
          // for some reason, for enterprises that pay with subscriptions we shouldn't <require> job id/licensee
          if (this.selectedPaymentMethod === 'subscription_token') {
            return true
          } else {
            return this.jobId && this.licensee
          }
        }
        return this.jobId && this.licensee
      }

      return true
    },

    buyersSelectOptions() {
      if (this.permissions.available_buyers) {
        return this.permissions.available_buyers.map(el => {
          return {
            value: el.id,
            text: `${el.first_name} ${el.last_name}`
          }
        })
      } else {
        return []
      }
    },

    customAgreementSelected() {
      return !this.licenseAgreement || !['royalty_free', 'comp'].includes(this.licenseAgreement)
    },

    canChooseCustomLicenseAgreement() {
      return this.hasCustomLicenseAgreements && this.selectedVersion !== 'comp'
    },

    hasCustomLicenseAgreements() {
      return this.licenseAgreements && this.licenseAgreements.length > 0
    },

    availableLicenseAgreements() {
      if (this.selectedVersion === 'comp') {
        return [{
          value: 'comp',
          text: 'Unwatermarked Comp License'
        }]
      }
      const standard = {
        value: "royalty_free",
        text: "Standard Royalty-Free"
      }

      return [
        ...[standard],
        ...this.licenseAgreements.map(el => {
          return {
            value: el.id,
            text: el.name
          }
        })
      ]
    },

    // Returns array with possible options: [subscription_token, currency, new_subscription]
    availablePaymentMethods() {
      let options = []
      let version = this.selectedVersion
      if (!version) return options;

      if (!window.POC.uid) {
        options.push('currency')
        return options
      }

      const payableWithSubscription = this.payableWithSubscription(version)


      if (payableWithSubscription) {
        options.push('subscription_token')
      } else {
        if (this.permissions.allow_credit_card) {
          options.push('currency')
        }
      }

      return options
    },

    newSubscriptionOptionVisible() {
      let version = this.selectedVersion
      if (!version) return false;
      return !this.payableWithSubscription(version)
    },

    newSubscriptionOptionText() {
      if (this.activeSubscription) {
        return "Upgrade Subscription to save up to 66%"
      }

      return "Create a new subscription"
    },

    creditVersionSizeProducts() {
      // const sizes = this.sortArray(uniq(this.products.map(p => p.asset_version_size)), this.imageSizeHierarchy)
      const sizes = this.availableAssetSizes
      if (this.compTokenProduct) {
        return ['comp', ...sizes]
      } else {
        return sizes
      }
    },

    currentProduct() {
      if (!this.selectedVersion) return {}
      if (!this.selectedPaymentMethod) return {}
      if (this.selectedPaymentMethod === 'subscription_token') {
        if (this.selectedVersion === 'comp') {
          return this.compTokenProduct
        }

        // for subscription tokens the selected image version size doesn't match with all products (subs only have medium and xl)
        // pick the lowest version product that can be used with this selected version
        // in this branch there should be at least one product matching
        const sortedSubscriptionProductVersions = this.sortArray(uniq(this.products.filter(p => p.cost_type === 'subscription_token' && this.availableSubscriptionProductVersions.includes(p.asset_version_size)).map(p => p.asset_version_size)), this.imageSizeHierarchy)
        const sortedSubscriptionProducts = sortedSubscriptionProductVersions.map(v => this.products.find(p => p.cost_type === 'subscription_token' && p.asset_version_size === v))

        const selectedVersionHierarchyIndex = this.imageSizeHierarchy.indexOf(this.selectedVersion)

        const productVersionHierarchyIndexes = sortedSubscriptionProductVersions.map(v => this.imageSizeHierarchy.indexOf(v))

        const productHierarchyIndex = productVersionHierarchyIndexes.find(i => i >= selectedVersionHierarchyIndex)

        const productIdx = productVersionHierarchyIndexes.indexOf(productHierarchyIndex)

        return sortedSubscriptionProducts[productIdx] || {}
      } else {
        return this.productByVersionAndPaymentMethod(this.selectedVersion, this.selectedPaymentMethod)
      }

    },

    currentProductId() {
      return this.currentProduct ? this.currentProduct.id : null;
    },

    paramsOptions() {
      return {
        currency: {
          paramName: 'payment[product_id]',
          assetIdParamName: 'payment[provisioning_data][asset_id]',
          licenseAgreementParamName: 'payment[provisioning_data][license_agreement_id]',
          licenseeParamName: 'payment[provisioning_data][licensee]',
          jobIdParamName: 'payment[provisioning_data][job_id]',
          poNumberParamName: 'payment[provisioning_data][po_number]',
          formUrl: this.paymentsUrl
        },
        subscription_token: {
          paramName: 'subscription_exchange[product_id]',
          assetIdParamName: 'subscription_exchange[asset_id]',
          licenseAgreementParamName: 'subscription_exchange[license_agreement_id]',
          formUrl: this.subscriptionExchangesUrl
        }
      }
    },

    currentParamsOptions() {
      if (this.currentProduct) {
        const options = this.paramsOptions[this.currentProduct.cost_type];
        if (options) return options;
      }
      return {};
    },

    availableSubscriptionProductVersions() {
      return this.subscriptionProducts.map(p => p.asset_version_size)
    },

    versionSizeProducts() {
      if (this.selectedPaymentMethod === 'subscription_token') {
        return this.sortArray(uniq(this.products.filter(p => p.cost_type === 'subscription_token' && this.availableSubscriptionProductVersions.includes(p.asset_version_size)).map(p => p.asset_version_size)), this.imageSizeHierarchy)
      } else {
        return this.sortArray(uniq(this.products.map(p => p.asset_version_size)), this.imageSizeHierarchy)
      }
    },

    requestPurchaseCreated() {
      if (!this.permissions.purchase_requests || this.permissions.purchase_requests.length === 0) {
        return null
      }
      if (!this.selectedVersion) {
        return null
      }

      const version = this.sortArray(this.permissions.purchase_requests.map(el => el.version), this.imageSizeHierarchy)[0]
      const largestVersionRequest = this.permissions.purchase_requests.find(el => el.version === version)
      const requestHierarchyIndex = this.imageSizeHierarchy.indexOf(largestVersionRequest.version)
      const selectedHierarchyIndex = this.imageSizeHierarchy.indexOf(this.selectedVersion)
      if (selectedHierarchyIndex > requestHierarchyIndex) {
        return null
      }
      return largestVersionRequest

    },

    requestPurchaseCreatedStatus() {
      const r = this.requestPurchaseCreated
      if (r) {
        return this.humanize(r.status)
      }
      return null
    },

    largeSizeMetadata() {
      return this.imageSizes.commercial.large;
    },

    compTokenProduct() {
      // subscriptionProducts is empty when the user is a buyer
      if (this.subscriptionProducts.length === 0) {
        return this.products.find(p => p.identifier === "composite")
      }

      return this.subscriptionProducts.find(p => p.identifier === "composite")
    }
  },

  watch: {
    selectedVersion() {
      this.selectedPaymentMethod = this.availablePaymentMethods[0]
      this.selectDefaultLicenseAgreement()
    },
  },

  mixins: [GeneralMixins]
}
</script>